/*
 * Copyright (C) 2024 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and
 * should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly
 * prohibited.
 */
import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { Field, Form, Formik, FormikHelpers, FormikProps } from 'formik';
import { StyledProvisioningPageHeading } from '../../provisioning/StyledProvisioningPageHeading';
import { StyledProvisioningPageSubheading } from '../../provisioning/StyledProvisioningPageSubheading';
import { Box, Button, Typography } from '@mui/material';
import { Link, useHistory, useLocation, useParams } from 'react-router-dom';
import { BeamInput } from '../../common/formik/BeamInput';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { BeamSelect } from '../../common/formik/BeamSelect';
import { BeamMultiSelect } from '../../common/formik/BeamMultiSelect';
import appTheme from '../../common/theme/appTheme.json';

import { HnaNetworkApprovalState, HnaRequest, HnaRequestStatus } from '../../../shared/entities/hnaRequest';
import * as yup from 'yup';
import { ajaxRaw } from '../../../utils/StoreUtils';
import { AjaxError, AjaxResponse } from 'rxjs/ajax';
import EditIcon from '@mui/icons-material/Edit';
import { makeStyles } from '@mui/styles';
import { BeamDatePicker } from '../../common/formik/BeamDatePicker';
import { HnaZoneSelector } from './HnaZoneSelector';
import { BeamSubmitButton } from '../../common/formik/BeamSubmitButton';
import { selectPlatformListItemsUnfiltered, selectTerminals, selectUserData, TerminalListItem } from '../../../store';
import { isNil } from 'lodash';
import Antenna from '../../../shared/entities/antenna';
import { EMPTY_FIELD_STRING_PLACEHOLDER } from '../../../utils/StringUtils';
import { Authorities } from '../../../shared/entities/user';
import { areAllowedRolesPresent } from '../../../utils/RBACUtils';
import { getTerminalsOnce } from '../../../store/terminals/actions';
import BeamSectionAlert, { BeamSectionAlertType } from "../../common/beam/BeamSectionAlert";

import { v4 as generateUuid } from 'uuid';

const useStyles = makeStyles({
  disclaimer: {
    color: appTheme.primaryTextColor,
    fontFamily: appTheme.appFontFamily,
    marginTop: '5px',
    marginBottom: '10px',
  },
  sectionHeaderStyle: {
    fontFamily: 'Uni Neue Light',
    fontStyle: 'normal',
    fontWeight: 'bold',
    fontSize: '20px',
    lineHeight: '28px',
    letterSpacing: 0.2,
    color: '#FFFFFF',
  },
});

type AntennaSelectOption = {
  label: string;
  value: string;
};

const HnaRequestsForm = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const isEditMode = location.pathname.includes('edit');
  const userData = useSelector(selectUserData, shallowEqual);
  const isApprover = areAllowedRolesPresent(userData, Authorities.ROLE_HNA_APPROVER);
  const classes = useStyles();
  let { uuid } = useParams();
  const formRef = useRef<FormikProps<HnaRequest>>(null);

  const terminals = useSelector(selectTerminals, shallowEqual);
  const terminalListItemsUnfiltered = useSelector(selectPlatformListItemsUnfiltered, shallowEqual);
  const [countries, setCountries] = useState<string[]>([]);
  const [antennas, setAntennas] = useState<Antenna[]>([]);
  // Show displayName in the dropdown, but keep the selected form value as antenna name.
  const antennaOptions: AntennaSelectOption[] = antennas.map((antenna: Antenna) => ({
    label: antenna.displayName,
    value: antenna.name,
  }));
  const [submissionError, setSubmissionError] = useState<string | undefined>(undefined);
  const [initialFormState, setInitialFormState] = useState<HnaRequest>({
    id: '',
    missionPlanId: '',
    requestorName: '',
    requestorOrgName: '',
    antennaType: '',
    missionNumber: '',
    tailNumbers: [],
    missionStartDate: '',
    missionEndDate: '',
    requestedAt: '',
    apacsId: '',
    additionalInfo: '',
    hnaZones: [
      {
        country: 'row' + generateUuid(),
        networks: [],
      },
    ],
    status: HnaRequestStatus.REQUESTED,
  });

  // Actions on initial load
  useEffect(() => {
    ajaxRaw.get('/mpt/hna-requests/countries').subscribe({
      next: value => setCountries(value.response),
      error: err => setSubmissionError(err.message),
    });
    ajaxRaw.get('/provisioning/options/antennas?filter').subscribe({
      next: value => setAntennas(value.response),
      error: err => setSubmissionError(err.message),
    });
  }, []);

  // Get terminal data once for the tail number selector
  useEffect(() => {
    dispatch(getTerminalsOnce());
  }, [dispatch]);

  // Request initial data when editing
  useEffect(() => {
    if (isEditMode && initialFormState.id === '') {
      ajaxRaw.get(`/mpt/hna-requests/${uuid}`).subscribe({
        next: value => setInitialFormState({ ...initialFormState, ...value.response }),
        error: err => setSubmissionError(err.message),
      });
    } else if (location.state && initialFormState.missionPlanId === '') {
      setInitialFormState({ ...initialFormState, ...location.state.partialHnaRequest });
    }
  }, [isEditMode, uuid, initialFormState, location.state]);

  // prefill user full name
  useEffect(() => {
    if (!isEditMode && initialFormState.requestorName === '' && userData?.claims.name) {
      // Okta has been observed to fill the `name` claim like "Last Name, First Name"
      let nameParts = userData.claims.name.split(', ');

      // If nameParts does not find a comma, just fill in original name from token.
      setInitialFormState({
        ...initialFormState,
        ...{ requestorName: nameParts[1] ? nameParts[1] + ' ' + nameParts[0] : userData.claims.name },
      });
    }
  }, [isEditMode, userData, initialFormState]);

  // prevent non-admin user from editing request that is past REQUESTED state
  useEffect(() => {
    if (isEditMode && !isApprover && initialFormState.status !== HnaRequestStatus.REQUESTED) {
      history.push(`/plans/hna-requests/view/${uuid}`);
    }
  }, [isEditMode, isApprover, initialFormState, history, uuid]);

  const tailOptions = (type: string) => {
    if (antennas[antennas.map(a => a.name).indexOf(type)]) {
      let missionAntennaTypeId = antennas[antennas.map(a => a.name).indexOf(type)].id;
      return terminalListItemsUnfiltered
        .filter((terminalListItem: TerminalListItem) =>
          terminalListItem.datapathUuidsInGroup?.some(
            (datapathUuid: string) =>
              !isNil(terminals[datapathUuid]?.antennaTypeId) &&
              `${terminals[datapathUuid]?.antennaTypeId}` === `${missionAntennaTypeId}`,
          ),
        )
        .map(
          (terminalListItem: TerminalListItem) =>
            terminalListItem?.terminalDisplayName || EMPTY_FIELD_STRING_PLACEHOLDER,
        );
    } else {
      return [];
    }
  };

  return (
    <>
      <HnaRequestHeader title={isEditMode ? 'Edit HNA Request' : 'New HNA Request'} editButton={false} />
      <div style={{ width: '830px', margin: '0 auto' }}>
        <Formik
          innerRef={formRef}
          enableReinitialize={true}
          initialValues={initialFormState}
          onSubmit={(values, { setSubmitting }: FormikHelpers<HnaRequest>) => {
            setSubmissionError(undefined);
            setSubmitting(true);

            let url = '/mpt/hna-requests';
            let operation = ajaxRaw.postJson;
            let payload = JSON.stringify(values);

            if (isEditMode) {
              url = `/mpt/hna-requests/${values.id}`;
              operation = ajaxRaw.put;
            } else {
              let cleanedRequest = values as Partial<HnaRequest>;
              delete cleanedRequest.id;
              delete cleanedRequest.requestedAt;
              payload = JSON.stringify(cleanedRequest);
            }

            operation(url, payload).subscribe({
              next: (response: AjaxResponse<any>) => {
                if (response.status !== 200 && response.status !== 201) {
                  setSubmissionError(JSON.stringify(response.response));
                } else {
                  const hnaRequestResponse = response.response as HnaRequest;
                  history.push(`/plans/hna-requests/view/${hnaRequestResponse.id}`);
                }
                setSubmitting(false);
              },
              error: (err: AjaxError) => {
                setSubmissionError(err.response.detail ? err.response.detail : err.message);
                setSubmitting(false);
              },
            });
          }}
          validationSchema={yup.object().shape({
            requestorName: yup
              .string()
              .required('Please enter a requestor name.')
              .max(255, 'Requestor name must be less than 255 characters.'),
            requestorOrgName: yup
              .string()
              .required('Please enter a requestor organization or squadron')
              .max(255, 'Organization name must be less than 255 characters.'),
            antennaType: yup.string().required('Please select an antenna type.'),
            missionNumber: yup
              .string()
              .required('Please enter a mission number.')
              .max(255, 'Mission number must be less than 255 characters.'),
            tailNumbers: yup.array().of(yup.string()).min(1, 'Please select tail number(s).'),
            missionStartDate: yup.date().required('Please select mission start date.'),
            missionEndDate: yup
              .date()
              .required('Please select mission end date.')
              .min(yup.ref('missionStartDate'), "End date can't be before start date."),
            apacsId: yup
              .string()
              .required('Please enter APACS ID.')
              .max(255, 'APACS ID must be less than 255 characters.'),
            hnaZones: yup
              .array()
              .of(
                yup.object().shape({
                  country: yup.string(),
                  networks: yup
                    .array()
                    .of(
                      yup.object().shape({
                        network: yup.string(),
                        state: yup.string<HnaNetworkApprovalState>(),
                      }),
                    )
                    .min(1),
                }),
              )
              .min(1),
          })}
        >
          {({ isSubmitting, values }) => (
            <Form
              style={{ width: '800px', paddingLeft: '16px', display: 'flex', flexDirection: 'column', rowGap: '15px' }}
            >
              <Box>
                <StyledProvisioningPageHeading>
                  {isEditMode ? '' : 'Input '}HNA Request Details
                </StyledProvisioningPageHeading>
                <StyledProvisioningPageSubheading>* All fields are required.</StyledProvisioningPageSubheading>
              </Box>
              <Typography className={classes.sectionHeaderStyle}>Requestor information</Typography>
              <Typography>Requestor name and organization cannot be changed after creation.</Typography>
              <Box display="flex" justifyContent="space-between" columnGap={'15px'}>
                <FlexDuoWrapper>
                  <BeamInput name={'requestorName'} label={'Full Name'} required={true} disableInput={isEditMode} />
                </FlexDuoWrapper>
                <FlexDuoWrapper>
                  <BeamInput
                    name={'requestorOrgName'}
                    label={'Organization/Squadron'}
                    required={true}
                    disableInput={isEditMode}
                  />
                </FlexDuoWrapper>
              </Box>
              <Typography className={classes.sectionHeaderStyle}> Mission information </Typography>
              <Typography>Note: Available tail numbers are based on your mission’s antenna type.</Typography>
              <Box display="flex" justifyContent="space-between" flexWrap="wrap" rowGap="20px" columnGap={'15px'}>
                <FlexSoloWrapper>
                  <BeamSelect
                    name={'antennaType'}
                    label={'Antenna Type'}
                    options={antennaOptions}
                    // Formik stores antennaType as a string name, so map that value back to the display label.
                    getOptionLabel={option => {
                      if (typeof option === 'string') {
                        return (
                          antennaOptions.find((antennaOption: AntennaSelectOption) => antennaOption.value === option)
                            ?.label || option
                        );
                      }

                      return option.label;
                    }}
                    isOptionEqualToValue={(option: AntennaSelectOption, value: AntennaSelectOption | string) =>
                      option.value === (typeof value === 'string' ? value : value.value)
                    }
                    placeholder={'Select an antenna type'}
                    onChange={(_, antennaOption: AntennaSelectOption | null) => {
                      // Reset tail selections when antenna changes and store backend-compatible antenna name.
                      values.tailNumbers = [];
                      return antennaOption?.value || '';
                    }}
                    required={true}
                  />
                </FlexSoloWrapper>
                <FlexDuoWrapper>
                  <BeamInput name={'missionNumber'} label={'Mission Number'} required={true} />
                </FlexDuoWrapper>
                <FlexDuoWrapper>
                  <BeamMultiSelect
                    name={'tailNumbers'}
                    label={'Tail number(s)'}
                    options={tailOptions(values.antennaType)}
                    disabled={values.antennaType ? false : true}
                    placeholder={values.antennaType ? 'Select tail(s)' : 'Select an antenna type first'}
                    getOptionLabel={option => option}
                    required={true}
                  />
                </FlexDuoWrapper>
                <FlexDuoWrapper>
                  <BeamDatePicker name={'missionStartDate'} label={'Mission Start Date'} required={true} />
                </FlexDuoWrapper>
                <FlexDuoWrapper>
                  <BeamDatePicker name={'missionEndDate'} label={'Mission End Date'} required={true} />
                </FlexDuoWrapper>
                <FlexSoloWrapper>
                  <BeamInput name={'apacsId'} label={'APACS'} placeholder={'Enter mission APACS ID'} required={true} />
                </FlexSoloWrapper>
                <FlexFullWrapper>
                  <BeamInput
                    name={'additionalInfo'}
                    multiline={true}
                    label={'Additional Information (optional)'}
                    required={false}
                    placeholder={'Enter additional mission information or specific HCX requests'}
                  />
                </FlexFullWrapper>
              </Box>
              <Typography className={classes.sectionHeaderStyle}> Network information </Typography>
              <Field name="hnaZones" component={HnaZoneSelector} countries={countries} />

              {submissionError ? (
                <BeamSectionAlert type={BeamSectionAlertType.ERROR}>{submissionError}</BeamSectionAlert>
              ) : (
                <></>
              )}

              <Box display="flex" alignItems={'center'} justifyContent={'center'} columnGap={'50px'}>
                <Button
                  variant="contained"
                  size="large"
                  disabled={isSubmitting}
                  sx={{ backgroundColor: '#141D24', height: '40px', width: '210px' }}
                  onClick={() => {
                    history.goBack();
                  }}
                >
                  Cancel
                </Button>
                <BeamSubmitButton text={(isEditMode ? 'Update' : 'Submit') + ' HNA Request'} width="210px" />
              </Box>
            </Form>
          )}
        </Formik>
      </div>
    </>
  );
};

export default HnaRequestsForm;

// Wrapper div for two items in a flexbox row
export const FlexDuoWrapper = styled.div`
  flex-basis: 48%;
`;

// Wrapper div to create a half-length flexbox item that sits in its own row
export const FlexSoloWrapper = styled.div`
  flex-basis: 48%;
  margin-right: 100px;
`;

// Wrapper div for a full length item in a flexbox row
export const FlexFullWrapper = styled.div`
  flex-basis: 100%;
`;

interface HnaRequestHeaderProps {
  title: string;
  handleEdit?: () => {};
  editButton?: boolean;
  disableEditButton?: boolean;
}

const BreadcrumbPath = styled(Link)`
  font-family: ${appTheme.viasatFontFamilyRegular};
  color: ${appTheme.primaryTextColor};
  font-weight: 400;
  font-size: 18px;
  letter-spacing: 0.2px;
  text-decoration: none;
  margin: 0;
`;

const BreadcrumbLeaf = styled.span`
  font-family: ${appTheme.viasatFontFamilyHeavy};
  color: #fff;
  font-size: 18px;
  font-style: normal;
  font-weight: 800;
  letter-spacing: 0.2px;
`;

export const HnaRequestHeader = ({ title, handleEdit, editButton, disableEditButton }: HnaRequestHeaderProps) => {
  return (
    <Box display="flex" justifyContent={'space-between'} marginBottom={'20px'}>
      <Box paddingTop={'0.5px'}>
        <BreadcrumbPath to={'/plans/hna-requests'}>HNA Requests {'> '}</BreadcrumbPath>
        <BreadcrumbLeaf>{title}</BreadcrumbLeaf>
      </Box>
      {editButton ? (
        <Button
          disabled={disableEditButton}
          onClick={handleEdit}
          startIcon={<EditIcon />}
          variant="outlined"
          color="secondary"
          sx={{
            borderRadius: '1000px',
            color: '#ffffff',
            lineHeight: '24px',
            letterSpacing: 0,
            textAlign: 'center',
          }}
        >
          Edit HNA Request
        </Button>
      ) : (
        <></>
      )}
    </Box>
  );
};
